import React, { useState } from 'react';
import { X, Truck, User, MapPin, Loader, ShoppingBag, TriangleAlert as AlertTriangle } from 'lucide-react';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { CART_CHECKOUT_URL } from '../../config/api';
import './CartCheckout.css';

interface CartSummary {
  total_items: number;
  subtotal: number;
  delivery_fee: number;
  tax: number;
  grand_total: number;
}

interface CartCheckoutProps {
  summary: CartSummary | null;
  onClose: () => void;
  onSuccess: (data: any) => void;
}

const CartCheckout: React.FC<CartCheckoutProps> = ({ summary, onClose, onSuccess }) => {
  const [deliveryType, setDeliveryType] = useState<'delivery_partner' | 'self_pickup'>('delivery_partner');
  const paymentMethod = 'cod' as const;
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (): boolean => {
    if (deliveryType === 'delivery_partner') {
      if (!deliveryAddress.trim()) {
        setError('Please enter your delivery address.');
        return false;
      }
      if (deliveryAddress.trim().length < 10) {
        setError('Please provide a complete delivery address (at least 10 characters).');
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const body: Record<string, string> = {
        payment_method: paymentMethod,
        delivery_type: deliveryType,
      };
      body.delivery_address = deliveryType === 'delivery_partner' ? deliveryAddress.trim() : '';

      const res = await authenticatedFetch(CART_CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const json = await res.json();

      if (res.ok && json.success) {
        onSuccess({
          ...json,
          delivery_type: deliveryType,
          payment_method: paymentMethod,
          delivery_address: deliveryType === 'delivery_partner' ? deliveryAddress.trim() : undefined,
        });
      } else {
        if (res.status === 422) {
          setError(json.detail?.[0]?.msg || 'Validation error. Please check your details.');
        } else {
          setError(json.message || 'Checkout failed. Please try again.');
        }
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="checkout-overlay" onClick={handleOverlayClick}>
      <div className="checkout-modal">
        <div className="checkout-header">
          <button className="checkout-close-btn" onClick={onClose} disabled={isSubmitting}>
            <X size={22} />
          </button>
          <h2 className="checkout-title">Checkout</h2>
          <p className="checkout-subtitle">Complete your order details below</p>
        </div>

        <form className="checkout-form" onSubmit={handleSubmit}>
          {summary && (
            <div className="checkout-summary-strip">
              <span>{summary.total_items} item{summary.total_items !== 1 ? 's' : ''}</span>
              <span className="checkout-summary-total">₹{summary.grand_total.toFixed(2)}</span>
            </div>
          )}

          {/* Delivery Type */}
          <div className="checkout-section">
            <h3 className="checkout-section-title">
              <Truck size={18} />
              Delivery Type
            </h3>
            <div className="checkout-options">
              <label className={`checkout-option ${deliveryType === 'delivery_partner' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="delivery_partner"
                  checked={deliveryType === 'delivery_partner'}
                  onChange={() => setDeliveryType('delivery_partner')}
                  disabled={isSubmitting}
                />
                <div className="option-content">
                  <Truck size={18} />
                  <div>
                    <span className="option-title">Delivery Partner</span>
                    <span className="option-desc">Delivered to your address</span>
                  </div>
                </div>
              </label>
              <label className={`checkout-option ${deliveryType === 'self_pickup' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="self_pickup"
                  checked={deliveryType === 'self_pickup'}
                  onChange={() => setDeliveryType('self_pickup')}
                  disabled={isSubmitting}
                />
                <div className="option-content">
                  <User size={18} />
                  <div>
                    <span className="option-title">Self Pickup</span>
                    <span className="option-desc">Pick up from cook's location</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Delivery Address */}
          {deliveryType === 'delivery_partner' && (
            <div className="checkout-section">
              <h3 className="checkout-section-title">
                <MapPin size={18} />
                Delivery Address
              </h3>
              <textarea
                className={`checkout-address-input ${error && !deliveryAddress.trim() ? 'input-error' : ''}`}
                value={deliveryAddress}
                onChange={e => { setDeliveryAddress(e.target.value); if (error) setError(''); }}
                placeholder="Enter your complete delivery address including house number, street, area, city and postal code..."
                rows={3}
                disabled={isSubmitting}
              />
              <p className="checkout-address-hint">
                Example: "123 Main Street, Near Central Park, Mumbai, 400001"
              </p>
            </div>
          )}

          {/* Payment Method */}
          <div className="checkout-section">
            <h3 className="checkout-section-title">
              <ShoppingBag size={18} />
              Payment Method
            </h3>
            <div className="checkout-options">
              <label className="checkout-option selected">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked
                  readOnly
                  disabled={isSubmitting}
                />
                <div className="option-content">
                  <span className="option-icon">💵</span>
                  <div>
                    <span className="option-title">Cash on Delivery</span>
                    <span className="option-desc">Pay when you receive your order</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {error && (
            <div className="checkout-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="checkout-actions">
            <button type="button" className="checkout-cancel-btn" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="checkout-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader size={16} className="checkout-spinner" />
                  Placing Order...
                </>
              ) : (
                <>
                  <ShoppingBag size={16} />
                  Place Order{summary ? ` — ₹${summary.grand_total.toFixed(2)}` : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CartCheckout;
